import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  useApproveQualification,
  useDeleteQualification,
  useRejectQualification,
  useReopenQualification,
} from "@/features/qualifications/hooks/use-qualifications";
import type {
  Qualification,
  QualificationStatus,
} from "@/features/qualifications/types";
import {
  ArrowCounterClockwiseIcon,
  CheckIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useTranslation } from "@/features/utils/i18n";
import { useMemo } from "react";

/**
 * What a reviewer does to several submissions at once. A decision is never final:
 * each action applies to the rows not already in that state, so a rejected one can
 * be approved, and any decided one put back to pending.
 */
export const useQualificationsBulkActions = (): BulkAction<Qualification>[] => {
  const { t } = useTranslation("qualifications");
  const isOwner = useHasRole("OWNER");
  const approveQualification = useApproveQualification();
  const rejectQualification = useRejectQualification();
  const reopenQualification = useReopenQualification();
  const deleteQualification = useDeleteQualification();

  return useMemo(() => {
    const not = (status: QualificationStatus) => (rows: Qualification[]) =>
      rows.filter((row) => row.status !== status);

    const decision = (
      key: "approve" | "reject" | "reopen",
      status: QualificationStatus,
      icon: React.ReactNode,
      mutation: { mutateAsync: (id: string) => Promise<unknown> },
      messages: { done: string; failed: string },
    ): BulkAction<Qualification> => ({
      key,
      label: t(`submissions.actions.${key}`),
      icon,
      isVisible: (rows) => not(status)(rows).length > 0,
      onAction: async (rows) => {
        await runBulk(
          not(status)(rows),
          (qualification) => mutation.mutateAsync(qualification.id),
          {
            success: (count) =>
              t(`submissions.bulk.${messages.done}`, { count }),
            error: (count) =>
              t(`submissions.bulk.${messages.failed}`, { count }),
          },
        );
      },
    });

    return [
      decision(
        "approve",
        "accepted",
        <CheckIcon className="h-4 w-4" />,
        approveQualification,
        { done: "approved", failed: "approve_failed" },
      ),
      decision(
        "reject",
        "rejected",
        <XIcon className="h-4 w-4" />,
        rejectQualification,
        { done: "rejected", failed: "reject_failed" },
      ),
      decision(
        "reopen",
        "pending",
        <ArrowCounterClockwiseIcon className="h-4 w-4" />,
        reopenQualification,
        { done: "reopened", failed: "reopen_failed" },
      ),
      ...(isOwner
        ? [
            {
              key: "delete",
              label: t("submissions.actions.delete"),
              icon: <TrashIcon className="h-4 w-4" />,
              variant: "destructive" as const,
              confirm: (rows: Qualification[]) =>
                t("submissions.bulk.delete_confirm", { count: rows.length }),
              onAction: async (rows: Qualification[]) => {
                await runBulk(
                  rows,
                  (qualification) =>
                    deleteQualification.mutateAsync(qualification.id),
                  {
                    success: (count) =>
                      t("submissions.bulk.deleted", { count }),
                    error: (count) =>
                      t("submissions.bulk.delete_failed", { count }),
                  },
                );
              },
            },
          ]
        : []),
    ];
  }, [
    t,
    isOwner,
    approveQualification,
    rejectQualification,
    reopenQualification,
    deleteQualification,
  ]);
};
